// Utilidades para formateo de datos

// Formatear moneda
export const formatCurrency = (amount, currency = 'MXN', locale = 'es-MX') => {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  } catch (error) {
    console.error('Error al formatear moneda:', error);
    return `$${amount.toFixed(2)}`;
  }
};

// Formatear dinero con formato americano (comas para miles, punto para decimales)
export const formatMoney = (amount, currency = '$', decimals = 2) => {
  // Convertir a número si es string
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;

  // Verificar si es un número válido
  if (isNaN(num) || num === null || num === undefined) {
    return `${currency}0.00`;
  }

  // Formatear el número con separadores de miles y decimales (formato americano)
  const formattedNumber = num.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });

  return `${currency}${formattedNumber}`;
};

// Formatear números
export const formatNumber = (number, decimals = 2, locale = 'es-MX') => {
  try {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(number);
  } catch (error) {
    console.error('Error al formatear número:', error);
    return number.toFixed(decimals);
  }
};

// Formatear fechas
export const formatDate = (date, options = {}, locale = 'es-MX') => {
  try {
    const defaultOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };

    const formatOptions = { ...defaultOptions, ...options };

    return new Intl.DateTimeFormat(locale, formatOptions).format(new Date(date));
  } catch (error) {
    console.error('Error al formatear fecha:', error);
    return new Date(date).toLocaleDateString();
  }
};

// Formatear fecha y hora
export const formatDateTime = (date, locale = 'es-MX') => {
  return formatDate(date, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }, locale);
};

// Formatear solo hora
export const formatTime = (date, locale = 'es-MX') => {
  try {
    return new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).format(new Date(date));
  } catch (error) {
    console.error('Error al formatear hora:', error);
    return new Date(date).toLocaleTimeString();
  }
};

// Formatear código de barras
export const formatBarcode = (barcode) => {
  if (!barcode) return '';

  // Eliminar espacios y caracteres especiales
  const cleaned = barcode.toString().replace(/[^0-9]/g, '');

  // Formatear según longitud común de códigos de barras
  if (cleaned.length === 13) {
    // EAN-13: 123-456-789-012-3
    return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{3})(\d{1})/, '$1-$2-$3-$4-$5');
  } else if (cleaned.length === 12) {
    // UPC-A: 123-456-789-012
    return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{3})/, '$1-$2-$3-$4');
  } else if (cleaned.length === 8) {
    // EAN-8: 1234-5678
    return cleaned.replace(/(\d{4})(\d{4})/, '$1-$2');
  }

  return cleaned;
};

// Formatear nombre de producto (capitalizar)
export const formatProductName = (name) => {
  if (!name) return '';

  return name
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Formatear stock con unidades
export const formatStock = (quantity, unit = 'pz') => {
  return `${formatNumber(quantity, 0)} ${unit}`;
};

// Formatear porcentaje
export const formatPercentage = (value, decimals = 1) => {
  try {
    return `${formatNumber(value, decimals)}%`;
  } catch (error) {
    console.error('Error al formatear porcentaje:', error);
    return `${value.toFixed(decimals)}%`;
  }
};

// Formatear tamaño de archivo
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Formatear duración en milisegundos a legible
export const formatDuration = (ms) => {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
};